import React from 'react';
import { Container, Box, Typography, Card, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import { useScrollAnimation, fadeInUp } from '../hooks/useScrollAnimation';
import ExperienceCard from "./ExperienceCard";

const ExperienceSection = () => {
  const [titleRef, titleControls] = useScrollAnimation();
  const [expertiseRef, expertiseControls] = useScrollAnimation();
  const experiences = [
    {
        company: 'SVG Bundes-Zentralgenossenschaft',
        role: 'Senior Fullstack Developer & DevOps Engineer',
        period: 'Multiple Years',
        description:
            'Senior Fullstack Engineer with 8+ years of experience specializing in distributed systems and cloud-native architecture. Expert in bridging the gap between development and operations by implementing robust CI/CD pipelines and microservices using Java (Spring Boot) and C# (.NET Core). Proven track record of reducing deployment cycles and optimizing system performance for high-traffic applications.',
        achievements: [
            'monolithic .NET Framework application to a containerized ASP.NET Core microservices architecture',
            'Java/Spring Boot billing engine using asynchronous processing and Redis caching',
            'Designed and delivered a real-time dashboard using React and SignalR, allowing stakeholders to monitor',
            'Developed and documented secure, versioned RESTful APIs serving 5M+ requests daily, maintaining 99.99% uptime',
            'Designed a unified CI/CD pipeline in GitHub Actions for both Java and C# projects, integrating automated security scanning (SAST)',
            'Migrated monolithic application to a microservices architecture using Kubernetes Orchestration and Docker',
            'Built a highly scalable, fault-tolerant, and highly available system using AWS ECS and RDS',
            'Designed and implemented a highly scalable, fault-tolerant, and highly available system using AWS ECS and RDS',
        ],
        technologies: ['json', 'swagger', 'JavaScript', 'C#', 'Springboot', 'java'],
    },
    {
      company: 'Aras Corp',
      role: 'Senior Developer & Product Manager',
      period: 'Multiple Years',
      description:
        'Working with Aras PLM system as both developer and product manager. Deep expertise in building custom plugins, workflows, and complex deployment solutions. Led technical implementation while managing product requirements and stakeholder communications.',
      achievements: [
        'Developed custom PLM plugins and integrations',
        'Managed complex deployment scenarios',
        'Product management for PLM solutions',
        'Built automated workflows and business logic',
        'Technical architecture and system design',
      ],
      technologies: ['Aras Innovator', 'C#', 'JavaScript', 'SQL Server', 'PLM', 'Product Management'],
    },
    {
      company: 'Teamcenter PLM',
      role: 'PLM Developer & Integration Specialist',
      period: 'Multiple Years',
      description:
        'Specialized in Teamcenter PLM system development and enterprise integrations. Building custom solutions, plugins, and handling complex deployment challenges for large-scale enterprise environments.',
      achievements: [
        'Custom Teamcenter plugin development',
        'Enterprise system integrations',
        'Complex deployment automation',
        'Custom workflow implementations',
        'Performance optimization and scaling',
      ],
      technologies: ['Teamcenter', 'Java', 'C++', 'Oracle', 'PLM', 'Enterprise Integration'],
    },
    {
      company: 'Enterprise Solutions',
      role: 'Senior Fullstack Developer & DevOps Engineer',
      period: '10+ Years',
      description:
        'Full-stack development across web, mobile, and desktop platforms with strong DevOps practices. Specialized in building scalable applications, implementing CI/CD pipelines, and managing cloud infrastructure.',
      achievements: [
        'Built 150+ successful projects',
        'Docker & Kubernetes expertise',
        'CI/CD pipeline implementation with GitHub Actions',
        'Linux server administration and configuration',
        'Desktop application development',
        'XRechnung and ZUGFeRD services implementation',
        'WordPress and Joomla custom development',
      ],
      technologies: [
        'C#',
        'Java',
        'JavaScript',
        'React',
        'Node.js',
        'Docker',
        'Kubernetes',
        'Linux',
        'CI/CD',
        'AWS',
        'Azure',
      ],
    },
  ];

  const expertise = [
    {
      category: 'Development',
      items: [
        'C# and .NET Framework',
        'Java (Spring Boot)',
        'JavaScript/TypeScript',
        'Python',
        'Desktop Applications',
        'Mobile Development',
      ],
    },
    {
      category: 'DevOps & Infrastructure',
      items: [
        'Docker Configuration',
        'Kubernetes Orchestration',
        'Linux Server Administration',
        'CI/CD Pipelines',
        'GitHub Actions',
        'Build Image Creation',
      ],
    },
    {
      category: 'PLM & Enterprise',
      items: [
        'Aras Corp PLM',
        'Teamcenter',
        'Plugin Development',
        'Complex Deployments',
        'Product Management',
        'Enterprise Integration',
      ],
    },
    {
      category: 'Specialized Services',
      items: [
        'XRechnung Services',
        'ZUGFeRD Implementation',
        'WordPress Development',
        'Joomla Customization',
        'CMS Plugin Development',
        'Document Processing',
      ],
    },
  ];

    return (
        <Box
            id="experience"
            sx={{
                py: { xs: 10, md: 15 },
                background: 'rgba(0, 0, 0, 0.2)',
            }}
        >
            <Container maxWidth="xl">
                <Box
                    component={motion.div}
                    ref={titleRef}
                    initial="hidden"
                    animate={titleControls}
                    variants={fadeInUp}
                    sx={{ mb: 8, textAlign: 'center' }}
                >
                    <Typography
                        variant="h2"
                        sx={{
                            fontSize: { xs: '2.5rem', md: '4rem' },
                            mb: 2,
                        }}
                    >
                        Professional Experience
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'text.secondary',
                            maxWidth: '800px',
                            mx: 'auto',
                        }}
                    >
                        10+ years of delivering enterprise-grade solutions across multiple industries
                    </Typography>
                </Box>

                {/* Experience Timeline */}
                {experiences.map((exp, idx) => (
                    <ExperienceCard key={idx} exp={exp} idx={idx} />
                ))}

                {/* Expertise Highlights */}
                <Box
                    component={motion.div}
                    ref={expertiseRef}
                    initial="hidden"
                    animate={expertiseControls}
                    variants={fadeInUp}
                    sx={{ mt: 10 }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            mb: 5,
                            textAlign: 'center',
                            fontWeight: 700,
                        }}
                    >
                        Areas of Expertise
                    </Typography>
                    <Grid container spacing={3}>
                        {expertise.map((area, idx) => (
                            <Grid item xs={12} md={6} key={idx}>
                                <Card
                                    sx={{
                                        background: 'var(--card-bg)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid var(--border-subtle)',
                                        p: 3,
                                        height: '100%',
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            mb: 2,
                                            color: 'primary.main',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {area.category}
                                    </Typography>
                                    <Box component="ul" sx={{ pl: 2 }}>
                                        {area.items.map((item, itemIdx) => (
                                            <Typography
                                                component="li"
                                                key={itemIdx}
                                                variant="body2"
                                                sx={{
                                                    color: 'text.secondary',
                                                    mb: 1,
                                                    lineHeight: 1.6,
                                                }}
                                            >
                                                {item}
                                            </Typography>
                                        ))}
                                    </Box>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Container>
        </Box>
    );
};

export default ExperienceSection;

